import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography/index.tsx';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PickerCard } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';

import type { LeaderboardPicker_so5Leaderboard } from './__generated__/index.graphql.ts';

const GroupIcon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;

  &.md {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    margin-top: var(--intermediate-unit);
  }
`;

const GridIcon = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

const Grid = styled(Horizontal).attrs({ gap: 1, center: true })`
  flex-wrap: wrap;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-width: 460px;
  }
`;

type Props = {
  leaderboards: readonly LeaderboardPicker_so5Leaderboard[];
  selectedLeaderboard: LeaderboardPicker_so5Leaderboard;
  onSelect: (slug: string) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

export const LeaderboardPicker = ({
  leaderboards,
  selectedLeaderboard,
  onSelect,
  size = 'sm',
}: Props) => {
  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={selectedLeaderboard.so5LeaderboardType ?? undefined}
          size={size}
          icon={
            selectedLeaderboard.iconUrl ? (
              <GroupIcon
                className={size === 'md' ? 'md' : undefined}
                src={selectedLeaderboard.iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation="up"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose Competition" />
          </PickerHeader>
          <Grid>
            {leaderboards.map(leaderboard => (
              <PickerCard
                key={leaderboard.slug}
                selected={leaderboard.slug === selectedLeaderboard.slug}
                onClick={() => {
                  onSelect(leaderboard.slug);
                  closeDropdown();
                }}
              >
                {!!leaderboard.iconUrl && (
                  <GridIcon
                    src={leaderboard.iconUrl}
                    alt={leaderboard.displayName ?? ''}
                  />
                )}
                {!!leaderboard.displayName && (
                  <LabelL>{leaderboard.displayName}</LabelL>
                )}
              </PickerCard>
            ))}
          </Grid>
        </>
      )}
    </Dropdown>
  );
};

LeaderboardPicker.fragments = {
  so5Leaderboard: gql`
    fragment LeaderboardPicker_so5Leaderboard on So5Leaderboard {
      id
      slug
      so5LeaderboardType
      iconUrl
      displayName
    }
  ` as TypedDocumentNode<LeaderboardPicker_so5Leaderboard>,
};
